import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import ChevronLeftIcon from "../../icons/chevronLeft.icon";
import ChevronRightIcon from "../../icons/chevronRight.icon";
import categoryCore from "../../core/category.core";
import menuItemCore, { StorefrontMenuItem } from "../../core/menuItem.core";
import { categoryHasChildren, getMenuItemCategory } from "../../core/storefrontMenuCategory.core";
import { Category } from "../../types/category";
import { StorefrontMenuCategoryDropdown } from "./storefrontMenuCategoryDropdown.comp";
import { StorefrontMenuItemIcon } from "./storefrontMenuItemIcon.comp";
import { StorefrontMenuLink } from "./storefrontMenuLink.comp";

export default function StorefrontMenuSlider() {
  const router = useRouter();
  const [scrollPosition, setScrollPosition] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  const { data: menuItems = [], isLoading } = useQuery({
    queryKey: ["storefrontMenuItems", router.locale],
    queryFn: () => menuItemCore.list(),
    enabled: router.isReady,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories", router.locale],
    queryFn: () => categoryCore.getAllLevels(),
    enabled: router.isReady,
  });

  const [hoveredCategory, setHoveredCategory] = useState<Category | null>(null);

  useEffect(() => {
    const container = sliderRef.current;
    if (!container) return;
    const handleScroll = () => setScrollPosition(container.scrollLeft);
    container.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => container.removeEventListener("scroll", handleScroll);
  }, [menuItems]);

  const scroll = useCallback(
    (direction: "left" | "right") => {
      const container = sliderRef.current;
      if (!container) return;
      const scrollAmount = window.matchMedia("(max-width: 768px)").matches ? 180 : 300;
      const newPosition =
        direction === "left"
          ? Math.max(0, scrollPosition - scrollAmount)
          : Math.min(container.scrollWidth - container.clientWidth, scrollPosition + scrollAmount);
      container.scrollTo({ left: newPosition, behavior: "smooth" });
      setScrollPosition(newPosition);
    },
    [scrollPosition]
  );

  if (!menuItems.length) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="w-full bg-white relative z-40 py-8 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <div
      className="w-full bg-white relative z-40"
      onMouseLeave={() => setHoveredCategory(null)}
    >
      <div className="max-w-7xl mx-auto">
        <div className="relative px-8 md:px-12">
          <button
            type="button"
            onClick={() => scroll("left")}
            className="absolute -left-2 md:-left-4 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-1 md:p-2 
                     text-gray-600 hover:text-gray-800 shadow-md hover:shadow-lg transition-all duration-200"
            aria-label="Scroll left"
          >
            <ChevronLeftIcon className="w-4 h-4 md:w-6 md:h-6" />
          </button>

          <div ref={sliderRef} className="flex overflow-x-hidden gap-4 md:gap-8 py-4 md:py-6 scroll-smooth">
            {menuItems.map((item) => (
              <StorefrontMenuSliderItem
                key={item.id}
                item={item}
                categories={categories}
                onHoverCategory={setHoveredCategory}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => scroll("right")}
            className="absolute -right-2 md:-right-4 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-1 md:p-2 
                     text-gray-600 hover:text-gray-800 shadow-md hover:shadow-lg transition-all duration-200"
            aria-label="Scroll right"
          >
            <ChevronRightIcon className="w-4 h-4 md:w-6 md:h-6" />
          </button>
        </div>
      </div>

      {categoryHasChildren(hoveredCategory) && <StorefrontMenuCategoryDropdown category={hoveredCategory} />}
    </div>
  );
}

interface StorefrontMenuSliderItemProps {
  item: StorefrontMenuItem;
  categories: Category[];
  onHoverCategory: (category: Category | null) => void;
}

function StorefrontMenuSliderItem({ item, categories, onHoverCategory }: StorefrontMenuSliderItemProps) {
  const menuCategory = getMenuItemCategory(item, categories);
  const hasChildren = categoryHasChildren(menuCategory);

  return (
    <div
      className="flex-shrink-0 w-24 md:w-32"
      onMouseEnter={() => {
        if (hasChildren) onHoverCategory(menuCategory);
      }}
    >
      <StorefrontMenuLink href={item.href} className="block">
        <div className="flex flex-col items-center cursor-pointer group px-2 py-3">
          <div
            className="relative w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-yellow-400 bg-white flex items-center justify-center overflow-hidden
                       transition-all duration-300 ease-in-out hover:bg-gray-50"
          >
            <StorefrontMenuItemIcon iconMedia={item.iconMedia} label={item.label}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-6 h-6 md:w-8 md:h-8 text-gray-600"
              >
                <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
                <path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2" />
              </svg>
            </StorefrontMenuItemIcon>
          </div>
          <span className="mt-2 text-xs md:text-sm font-medium text-center line-clamp-2 h-8 md:h-10 text-gray-600">
            {item.label}
          </span>
        </div>
      </StorefrontMenuLink>
    </div>
  );
}
